(function () {
  if (window.__careRewardsInitialized) return;
  window.__careRewardsInitialized = true;

  const config = window.CareRewards || {};
  const REF_STORAGE_KEY = 'care_rewards_ref';
  const GOLD = '#d4af37';
  const GOLD_DARK = '#8b6914';
  const primaryColor = config.primaryColor || GOLD;
  document.documentElement.style.setProperty('--cr-primary', primaryColor);
  document.documentElement.style.setProperty('--cr-primary-dark', GOLD_DARK);

  function captureReferralCode() {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref) return;
    localStorage.setItem(REF_STORAGE_KEY, ref.trim().toUpperCase());
  }

  async function attachStoredReferral() {
    const ref = localStorage.getItem(REF_STORAGE_KEY);
    if (!ref) return null;

    try {
      return await attachReferralCode(ref, { clearStoredOnSuccess: true });
    } catch (e) {
      if (!String(e.message).includes('already')) {
        console.warn('Care Rewards referral:', e.message);
      }
      return null;
    }
  }

  let storedReferralPromise = null;

  function attachStoredReferralOnce() {
    if (!storedReferralPromise) {
      storedReferralPromise = attachStoredReferral();
    }
    return storedReferralPromise;
  }

  function normalizeReferralInput(value) {
    let code = String(value || '').trim().toUpperCase();
    if (!code) return '';

    if (code.startsWith('REFER-')) {
      const parts = code.split('-').filter(Boolean);
      if (parts.length >= 2) return parts[1].replace(/[^A-Z0-9]/g, '');
    }

    return code.replace(/[^A-Z0-9]/g, '');
  }

  function looksLikeReferralCode(value) {
    const raw = String(value || '').trim().toUpperCase();
    if (!raw || raw.startsWith('REWARDS-') || raw.startsWith('REFER-')) return false;
    const code = normalizeReferralInput(raw);
    return /^[A-Z0-9]{8}$/.test(code);
  }

  async function attachReferralCode(referralCode, options = {}) {
    const code = normalizeReferralInput(referralCode);
    if (!code) {
      throw new Error('Enter a referral code');
    }

    const payload = { action: 'attachReferral', referralCode: code };

    const res = await fetch(loyaltyApiUrl('/loyalty'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error('Rewards service is updating. Please try again in a minute.');
    }

    if (!res.ok || !data.success) {
      throw new Error(data?.error || 'Could not apply referral code');
    }

    if (options.clearStoredOnSuccess !== false) {
      localStorage.removeItem(REF_STORAGE_KEY);
    }
    return data;
  }

  function getCustomerId() {
    const id = config.customerId;
    if (id == null || id === '' || id === 'null' || id === 'undefined') return null;
    return id;
  }

  function proxyBaseUrl() {
    return (config.proxyUrl || '/apps/care-rewards/loyalty').replace(/\/loyalty\/?$/, '');
  }

  function loyaltyApiUrl(path = '/loyalty') {
    const params = new URLSearchParams();
    if (config.shop) params.set('shop', config.shop);
    const query = params.toString();
    return `${proxyBaseUrl()}${path}${query ? `?${query}` : ''}`;
  }

  const REFERRAL_LANDING_ORIGIN = 'https://eclectic-coins.com';

  function buildReferralLink(code) {
    return `${REFERRAL_LANDING_ORIGIN}?ref=${encodeURIComponent(code)}`;
  }

  function buildReferralQrCodeUrl(link) {
    return `${proxyBaseUrl()}/referral-qr?url=${encodeURIComponent(link)}`;
  }

  function updateReferralQrImage(wrap, link, compact = false) {
    if (!wrap || !link) return;

    let img = wrap.querySelector('img');
    if (!img) {
      img = document.createElement('img');
      img.className = 'care-rewards-referral__qr';
      img.alt = 'Scan to share your referral link';
      img.loading = 'lazy';
      wrap.insertBefore(img, wrap.firstChild);
    }

    img.src = buildReferralQrCodeUrl(link);
    img.width = compact ? 160 : 200;
    img.height = compact ? 160 : 200;
    wrap.hidden = false;
  }

  function referralQrMarkup(link, compact = false) {
    if (!link) return '';
    const size = compact ? 160 : 200;
    const qrUrl = buildReferralQrCodeUrl(link);
    return `
      <div class="care-rewards-referral__qr-wrap${compact ? ' care-rewards-referral__qr-wrap--compact' : ''}">
        <img
          class="care-rewards-referral__qr"
          src="${escapeHtml(qrUrl)}"
          alt="Scan to share your referral link"
          width="${size}"
          height="${size}"
          loading="lazy"
        />
        <p class="care-rewards-referral__qr-caption">Scan to share your referral link</p>
      </div>
    `;
  }

  function buildReferralSharePayload(link, code) {
    const message = `Join Eclectic Coins with my referral link and we both earn rewards!${code ? ` Code: ${code}` : ''}`;
    return {
      title: 'Eclectic Coins Rewards',
      text: message,
      url: link,
    };
  }

  function canUseNativeShare() {
    if (!navigator.share) return false;
    const isTouchDevice = navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches;
    const isNarrowScreen = window.matchMedia('(max-width: 749px)').matches;
    return isTouchDevice || isNarrowScreen;
  }

  function removeShareMenu() {
    document.getElementById('care-rewards-share-menu')?.remove();
  }

  function openShareFallbackMenu(link, code) {
    removeShareMenu();
    const payload = buildReferralSharePayload(link, code);
    const encodedText = encodeURIComponent(`${payload.text} ${link}`);
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    const menu = document.createElement('div');
    menu.id = 'care-rewards-share-menu';
    menu.className = `care-rewards-share-menu${isDesktop ? ' care-rewards-share-menu--desktop' : ''}`;
    menu.innerHTML = `
      <div class="care-rewards-share-menu__backdrop" data-share-close></div>
      <div class="care-rewards-share-menu__panel" role="dialog" aria-label="Share referral link">
        <p class="care-rewards-share-menu__title">Share your referral link</p>
        <div class="care-rewards-share-menu__options">
          <button type="button" class="care-rewards-share-menu__option" data-share-action="copy">Copy link</button>
          <a class="care-rewards-share-menu__option" href="mailto:?subject=${encodeURIComponent(payload.title)}&body=${encodedText}">Email</a>
          <a class="care-rewards-share-menu__option" href="sms:?body=${encodedText}">Text message</a>
          <a class="care-rewards-share-menu__option" href="https://wa.me/?text=${encodedText}" target="_blank" rel="noopener noreferrer">WhatsApp</a>
        </div>
        <button type="button" class="care-rewards-share-menu__cancel" data-share-close>Cancel</button>
      </div>
    `;

    document.body.appendChild(menu);

    menu.querySelector('[data-share-action="copy"]')?.addEventListener('click', async (event) => {
      const copyBtn = event.currentTarget;
      try {
        await navigator.clipboard.writeText(link);
        copyBtn.textContent = 'Copied!';
        window.setTimeout(removeShareMenu, 900);
      } catch (e) {
        window.prompt('Copy your referral link:', link);
        removeShareMenu();
      }
    });

    menu.querySelectorAll('[data-share-close]').forEach((el) => {
      el.addEventListener('click', removeShareMenu);
    });
  }

  async function shareReferralLink(link, code) {
    const payload = buildReferralSharePayload(link, code);
    if (canUseNativeShare()) {
      try {
        await navigator.share(payload);
        return;
      } catch (e) {
        if (e?.name === 'AbortError') return;
      }
    }
    openShareFallbackMenu(link, code);
  }

  function getReferralCodeFromGroup(container) {
    if (!container) return '';

    const codeBtn = container.querySelector('.care-rewards-btn--code');
    const fromBtn = codeBtn?.getAttribute('data-referral-code')?.trim();
    if (fromBtn) return fromBtn;

    const fromShare = container.querySelector('[data-share-code]')?.getAttribute('data-share-code')?.trim();
    if (fromShare) return fromShare;

    const shareLink = container.querySelector('[data-share-referral]')?.getAttribute('data-share-referral') || '';
    const match = shareLink.match(/[?&]ref=([^&]+)/);
    return match ? decodeURIComponent(match[1]).trim() : '';
  }

  function ensureReferralCodeDisplay(container) {
    let display = container.nextElementSibling;
    if (display?.classList?.contains('care-rewards-referral-code-display')) {
      return display;
    }

    display = document.createElement('p');
    display.className = 'care-rewards-referral-code-display';
    display.hidden = true;
    display.setAttribute('role', 'status');
    container.insertAdjacentElement('afterend', display);
    return display;
  }

  function toggleReferralCodeDisplay(btn) {
    const container = btn.closest('.care-rewards-referral-actions');
    if (!container) return;

    const code = getReferralCodeFromGroup(container);
    if (!code) return;

    const display = ensureReferralCodeDisplay(container);
    const isVisible = !display.hidden && display.textContent === code;

    if (isVisible) {
      display.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      return;
    }

    display.textContent = code;
    display.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
  }

  let shareDelegationReady = false;

  function initShareDelegation() {
    if (shareDelegationReady) return;
    shareDelegationReady = true;

    document.addEventListener('click', async (event) => {
      const btn = event.target.closest('[data-share-referral]');
      if (!btn) return;

      event.preventDefault();
      event.stopPropagation();

      const link = btn.getAttribute('data-share-referral');
      const code = btn.getAttribute('data-share-code') || '';
      if (!link) return;

      await shareReferralLink(link, code);
    });
  }

  let referralCodeDelegationReady = false;

  function initReferralCodeDelegation() {
    if (referralCodeDelegationReady) return;
    referralCodeDelegationReady = true;

    document.addEventListener('click', (event) => {
      const btn = event.target.closest('.care-rewards-btn--code');
      if (!btn) return;
      if (event.careRewardsCodeHandled) return;

      event.careRewardsCodeHandled = true;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      toggleReferralCodeDisplay(btn);
    }, true);
  }

  async function fetchLoyaltyData() {
    // App proxy adds logged_in_customer_id automatically for signed-in customers.
    // Do not append a bad customerId from theme config or it overrides Shopify's value.
    const res = await fetch(loyaltyApiUrl('/loyalty'), { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to load rewards');
    return res.json();
  }

  function cartReturnPath() {
    return window.location.pathname + window.location.search;
  }

  async function applyDiscountToCart(discountCode, redirectPath, options = {}) {
    const code = String(discountCode || '').trim();
    if (!code) throw new Error('No discount code was created');

    sessionStorage.setItem('care_rewards_applied_code', code);

    try {
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();
      const existing = (cart.discount_codes || []).map((entry) => entry.code).filter(Boolean);
      const discounts = existing.includes(code) ? existing : [...existing, code];

      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: discounts.join(',') }),
      });
      const updated = await updateRes.json();
      const discountEntry = (updated.discount_codes || []).find((entry) => entry.code === code);
      if (discountEntry?.applicable) {
        if (options.widget) {
          await confirmCartDiscountApplied(options.widget, code);
        } else if (options.reload !== false) {
          window.location.reload();
        }
        return updated;
      }

      if (discountEntry && !discountEntry.applicable) {
        throw new Error(
          `Discount code ${code} was not accepted by Shopify. Make sure you are signed in on the store, then try again.`,
        );
      }
    } catch (e) {
      if (e.message?.includes('was not accepted')) throw e;
      // Fall through to Shopify discount redirect.
    }

    const destination = redirectPath || cartReturnPath() || '/cart';
    const discountUrl = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(destination)}`;
    window.location.assign(discountUrl);
  }

  async function ensureRefereeDiscountCode() {
    const payload = { action: 'ensureRefereeDiscount' };

    const res = await fetch(loyaltyApiUrl('/loyalty'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error('Rewards service is updating. Please try again in a minute.');
    }

    if (!res.ok || !data.success) {
      throw new Error(data?.error || 'Could not create referral discount');
    }

    return data;
  }

  function rememberReferralRedemptionCode(discountCode) {
    if (!discountCode) return;
    sessionStorage.setItem('care_rewards_applied_code', discountCode);
  }

  function bindCopyReferralCodeButton(container) {
    if (!container) return;
    container.querySelector('[data-copy-referral-code]')?.addEventListener('click', async (event) => {
      const btn = event.currentTarget;
      const code = btn.getAttribute('data-copy-referral-code');
      if (!code) return;
      const original = btn.textContent;
      try {
        await navigator.clipboard.writeText(code);
        btn.textContent = 'Copied!';
      } catch {
        window.prompt('Copy your discount code:', code);
      }
      setTimeout(() => {
        btn.textContent = original;
      }, 2000);
    });
  }

  async function applyReferralDiscountToCart(discountCode, widget) {
    const applied = await applyShopifyDiscountCode(discountCode);
    if (applied.ok) {
      await confirmCartDiscountApplied(widget, discountCode);
      return true;
    }

    await applyDiscountToCart(discountCode, cartReturnPath(), { widget, reload: false });
    return true;
  }

  async function applyReferralFromCart(code, widget) {
    const referralCode = normalizeReferralInput(code);
    if (!referralCode) {
      throw new Error('Enter a referral code');
    }

    const result = await attachReferralCode(referralCode);
    sessionStorage.setItem('care_rewards_applied_referral', referralCode);

    let discountCode = result.discountCode || result.member?.refereeDiscountCode;
    if (!discountCode) {
      const ensured = await ensureRefereeDiscountCode();
      discountCode = ensured.discountCode;
    }

    if (!discountCode) {
      throw new Error('Referral linked, but no discount code was created. Please try again.');
    }

    rememberReferralRedemptionCode(discountCode);
    if (widget) {
      await applyReferralDiscountToCart(discountCode, widget);
    }

    return { ...result, discountCode };
  }

  function refreshCartSection(sectionId, html) {
    if (!sectionId || !html) {
      window.location.reload();
      return false;
    }

    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) {
      window.location.reload();
      return false;
    }

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.getElementById(`shopify-section-${sectionId}`);
    if (!next) {
      window.location.reload();
      return false;
    }

    section.innerHTML = next.innerHTML;
    return true;
  }

  async function applyShopifyDiscountCode(code) {
    const discountCode = String(code || '').trim();
    if (!discountCode) return { ok: false, reason: 'missing_code' };

    const component = document.querySelector('cart-discount-component');
    const sectionId = component?.dataset?.sectionId || null;
    const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((res) => res.json());
    const existing = (cart.discount_codes || []).map((entry) => entry.code).filter(Boolean);
    const discounts = existing.includes(discountCode) ? existing : [...existing, discountCode];

    const payload = { discount: discounts.join(',') };
    if (sectionId) payload.sections = [sectionId];

    const cartUpdateUrl = window.Theme?.routes?.cart_update_url || '/cart/update.js';
    const response = await fetch(cartUpdateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    const entry = (data.discount_codes || []).find((item) => item.code === discountCode);

    if (!entry?.applicable) {
      return { ok: false, reason: 'not_applicable', code: discountCode, data };
    }

    if (sectionId && data.sections?.[sectionId]) {
      refreshCartSection(sectionId, data.sections[sectionId]);
      return { ok: true, code: discountCode, data };
    }

    window.location.reload();
    return { ok: true, code: discountCode, data };
  }

  async function redeemReward(rewardId, options = {}) {
    const res = await fetch(`${proxyBaseUrl()}/redeem?shop=${encodeURIComponent(config.shop)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ rewardId }),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error('Rewards service is updating. Please try again in a minute.');
    }

    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Redemption failed');
    }
    if (!data.discountCode) {
      throw new Error('No discount code was created');
    }

    if (options.applyToCart) {
      await applyDiscountToCart(data.discountCode, options.redirectPath);
    }

    return data;
  }

  function showCartWidgetMessage(widget, message, tone = 'info', bodySelector = '.care-rewards-cart-redeem__body') {
    const existing = widget.querySelector('.care-rewards-cart-widget__status');
    if (existing) existing.remove();

    const el = document.createElement('p');
    el.className = 'care-rewards-cart-widget__status';
    if (tone === 'success') el.classList.add('is-success');
    if (tone === 'error') el.classList.add('is-error');
    el.textContent = message;
    widget.querySelector(bodySelector)?.prepend(el);
  }

  function showCartRedeemMessage(widget, message, tone = 'info') {
    showCartWidgetMessage(widget, message, tone, '.care-rewards-cart-redeem__body');
  }

  function showCartReferralMessage(widget, message, tone = 'info') {
    showCartWidgetMessage(widget, message, tone, '.care-rewards-cart-referral__body');
  }

  async function confirmCartDiscountApplied(widget, discountCode) {
    const showMessage =
      widget.id === 'care-rewards-cart-referral' ? showCartReferralMessage : showCartRedeemMessage;

    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((res) => res.json());
      const hasDiscount =
        (cart.total_discount && cart.total_discount > 0) ||
        (Array.isArray(cart.cart_level_discount_applications) &&
          cart.cart_level_discount_applications.length > 0);

      if (hasDiscount) {
        showMessage(widget, `Discount ${discountCode} applied to your cart.`, 'success');
        return;
      }
    } catch (e) {
      // Ignore cart refresh errors and still show the code.
    }

    showMessage(
      widget,
      `Discount code ${discountCode} created. It will apply at checkout if it is not shown above.`,
      'success',
    );
  }

  function bindRedeemButtons(container, memberPoints, redirectPath) {
    if (!container) return;
    container.querySelectorAll('[data-reward-id]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const rewardId = btn.getAttribute('data-reward-id');
        btn.disabled = true;
        const original = btn.textContent;
        btn.textContent = 'Applying...';
        try {
          const result = await redeemReward(rewardId, { redirectPath, applyToCart: true });
          const balanceEl = document.querySelector('.care-rewards-balance__amount');
          if (balanceEl && result.member?.pointsBalance != null) {
            balanceEl.textContent = result.member.pointsBalance.toLocaleString();
          }
          renderActivity(
            result.transactions || [],
            document.getElementById('care-rewards-activity'),
            result.member?.redemptionCodes,
          );
        } catch (e) {
          btn.disabled = false;
          btn.textContent = original;
          alert(e.message || 'Could not redeem reward');
        }
      });
    });
  }

  const NON_CLAIMABLE_EARN_TYPES = new Set(['purchase', 'referral']);
  const EARN_ACTION_URLS = {
    instagram: 'https://www.instagram.com/eclecticcoins/',
    facebook: 'https://www.facebook.com/profile.php?id=61582189867557',
    twitter: 'https://x.com/',
    tiktok: 'https://www.tiktok.com/@eclectic.coins',
    newsletter: 'https://eclectic-coins.com/pages/goldback-tracker-app',
    review: 'https://www.reddit.com/r/Goldback/',
    birthday: 'https://eclectic-coins.com/account',
  };

  function inferEarnActionUrl(rule) {
    const text = `${rule.title || ''} ${rule.description || ''}`.toLowerCase();
    if (text.includes('youtube')) {
      return 'https://www.youtube.com/@GoldbackGurus?sub_confirmation=1';
    }
    if (text.includes('reddit')) {
      return 'https://www.reddit.com/r/Goldback/';
    }
    if (text.includes('instagram')) return EARN_ACTION_URLS.instagram;
    if (text.includes('facebook')) return EARN_ACTION_URLS.facebook;
    if (text.includes('tiktok')) return EARN_ACTION_URLS.tiktok;
    if (
      text.includes('tracker') ||
      text.includes('goldback tracker') ||
      text.includes('newsletter') ||
      text.includes('email')
    ) {
      return EARN_ACTION_URLS.newsletter;
    }
    return EARN_ACTION_URLS[rule.type] || null;
  }

  function getEarnActionUrl(rule) {
    try {
      const config = JSON.parse(rule.config || '{}');
      if (config.url) return config.url;
    } catch (e) {
      // ignore invalid config
    }
    return inferEarnActionUrl(rule);
  }

  function isEarnRuleCompleted(rule, earnClaims) {
    if (!earnClaims) return false;
    if (rule.type === 'signup') return Boolean(earnClaims.signup);
    if (rule.isRepeatable) return false;
    return Boolean(earnClaims[rule.type]);
  }

  async function claimEarnRule(ruleType) {
    const res = await fetch(`${proxyBaseUrl()}/loyalty?shop=${encodeURIComponent(config.shop)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ ruleType }),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error('Rewards service is updating. Please try again in a minute.');
    }

    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Could not claim points');
    }
    return data;
  }

  function renderEarnRules(rules, container, options = {}) {
    if (!container) return;
    const { member, earnClaims } = options;
    const returnUrl = encodeURIComponent(window.location.pathname);

    container.innerHTML = rules.map((rule) => {
      const isStatic = NON_CLAIMABLE_EARN_TYPES.has(rule.type);
      const isReferral = rule.type === 'referral';
      const isSignup = rule.type === 'signup';
      const completed = isEarnRuleCompleted(rule, earnClaims);
      const actionUrl = getEarnActionUrl(rule);
      const canClaim = Boolean(member) && !isStatic && !isSignup && !completed;
      const canOpenAction = !member && !isStatic && !isSignup && !completed && Boolean(actionUrl);
      const isInteractive = canClaim || canOpenAction;
      const referralLink = isReferral && member ? buildReferralLink(member.referralCode) : null;
      const cardClasses = [
        'care-rewards-card',
        'care-rewards-earn-card',
        completed ? 'is-completed' : '',
        isInteractive ? 'is-clickable' : '',
        isStatic ? 'is-static' : '',
        isReferral ? 'is-referral' : '',
      ].filter(Boolean).join(' ');

      let pointsLabel = '';
      if (rule.type === 'purchase') {
        pointsLabel = '<span class="care-rewards-card__points">Per $1 spent</span>';
      } else if (rule.type !== 'referral' && rule.points > 0) {
        pointsLabel = `<span class="care-rewards-card__points">+${rule.points} pts</span>`;
      }

      let footer = '';
      if (completed) {
        footer = '<span class="care-rewards-earn-card__status">Completed</span>';
      } else if (canClaim) {
        footer = `<span class="care-rewards-earn-card__cta">${actionUrl ? 'Tap to start' : 'Tap to claim points'}</span>`;
      } else if (canOpenAction) {
        footer = '<span class="care-rewards-earn-card__signin">Sign in to earn</span>';
      } else if (referralLink) {
        footer = `
          <div class="care-rewards-referral__earn-content">
            <div class="care-rewards-referral-actions">
              <button type="button" class="care-rewards-btn care-rewards-btn--share" data-share-referral="${escapeHtml(referralLink)}" data-share-code="${escapeHtml(member.referralCode)}">Share</button>
              <button type="button" class="care-rewards-btn care-rewards-btn--secondary care-rewards-btn--code" data-referral-code="${escapeHtml(member.referralCode)}" aria-expanded="false" aria-label="Show referral code">Code</button>
            </div>
            <p class="care-rewards-referral-code-display" hidden>${escapeHtml(member.referralCode)}</p>
            ${referralQrMarkup(referralLink, true)}
          </div>
        `;
      } else if (!member && !isStatic && !isSignup) {
        footer = `<a class="care-rewards-earn-card__signin" href="/account/login?return_url=${returnUrl}">Sign in to earn</a>`;
      } else if (isSignup && member && !completed) {
        footer = '<span class="care-rewards-earn-card__hint">Awarded when you join</span>';
      }

      const interactiveAttrs = isInteractive
        ? `data-earn-type="${escapeHtml(rule.type)}"${actionUrl ? ` data-earn-url="${escapeHtml(actionUrl)}"` : ''}${canOpenAction ? ' data-earn-guest="true"' : ''} role="button" tabindex="0" aria-label="${actionUrl ? 'Open ' : 'Complete '}${escapeHtml(rule.title)}"`
        : '';

      return `
        <div
          class="${cardClasses}"
          ${interactiveAttrs}
        >
          ${completed ? '<span class="care-rewards-earn-card__check" aria-hidden="true">✓</span>' : ''}
          <p class="care-rewards-card__title">${escapeHtml(rule.title)}</p>
          <p class="care-rewards-card__desc">${escapeHtml(rule.description || '')}</p>
          ${pointsLabel}
          ${footer}
        </div>
      `;
    }).join('');

    bindEarnCards(container, options);
  }

  function bindEarnCards(container, options = {}) {
    if (!container) return;

    const refresh = options.onRefresh || (() => {});

    container.querySelectorAll('[data-earn-type]').forEach((card) => {
      const handleClaim = async () => {
        if (card.classList.contains('is-completed') || card.classList.contains('is-claiming')) return;

        const ruleType = card.getAttribute('data-earn-type');
        const actionUrl = card.getAttribute('data-earn-url');
        const isGuest = card.getAttribute('data-earn-guest') === 'true';

        if (isGuest) {
          if (actionUrl) {
            window.open(actionUrl, '_blank', 'noopener,noreferrer');
          }
          return;
        }
        const cta = card.querySelector('.care-rewards-earn-card__cta');
        const originalCta = cta?.textContent;
        const actionOpened = card.getAttribute('data-action-opened') === 'true';

        if (actionUrl && !actionOpened) {
          window.open(actionUrl, '_blank', 'noopener,noreferrer');
          card.setAttribute('data-action-opened', 'true');
          if (cta) cta.textContent = 'I completed this — claim points';
          return;
        }

        card.classList.add('is-claiming');
        if (cta) cta.textContent = 'Claiming...';

        try {
          const result = await claimEarnRule(ruleType);
          const rule = (options.rules || []).find((r) => r.type === ruleType);
          const isRepeatable = rule?.isRepeatable;

          if (!isRepeatable) {
            card.classList.add('is-completed');
            card.classList.remove('is-clickable', 'is-claiming');
            card.removeAttribute('data-earn-type');
            card.removeAttribute('data-action-opened');
            card.removeAttribute('data-earn-url');
            card.removeAttribute('role');
            card.removeAttribute('tabindex');

            if (!card.querySelector('.care-rewards-earn-card__check')) {
              card.insertAdjacentHTML('afterbegin', '<span class="care-rewards-earn-card__check" aria-hidden="true">✓</span>');
            }

            if (cta) {
              cta.className = 'care-rewards-earn-card__status';
              cta.textContent = 'Completed';
            }
          } else if (cta) {
            card.classList.remove('is-claiming');
            cta.textContent = originalCta || 'Tap to claim again';
          }

          refresh(result);
        } catch (e) {
          card.classList.remove('is-claiming');
          if (cta) cta.textContent = originalCta || 'Tap to claim';
          if (!String(e.message).includes('Already claimed')) {
            alert(e.message || 'Could not claim points');
          }
        }
      };

      card.addEventListener('click', handleClaim);
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClaim();
        }
      });
    });
  }

  function renderRewards(rewards, container, memberPoints) {
    if (!container) return;
    container.innerHTML = rewards.map(reward => {
      const canRedeem = memberPoints >= reward.pointsCost;
      return `
        <div class="care-rewards-card">
          <p class="care-rewards-card__title">${escapeHtml(reward.title)}</p>
          <p class="care-rewards-card__desc">${escapeHtml(reward.description || '')}</p>
          <span class="care-rewards-card__points">${reward.pointsCost} pts</span>
          ${getCustomerId() && canRedeem ? `<button type="button" class="care-rewards-btn care-rewards-btn--primary" style="margin-top:12px" data-reward-id="${reward.id}">Redeem</button>` : ''}
        </div>
      `;
    }).join('');
  }

  function renderVipTiers(tiers, container, activeTierId) {
    if (!container) return;
    container.innerHTML = tiers.map(tier => `
      <div class="care-rewards-tier${activeTierId && tier.id === activeTierId ? ' is-active' : ''}">
        ${activeTierId && tier.id === activeTierId ? '<span class="care-rewards-tier__active-badge">Your tier</span>' : ''}
        <div class="care-rewards-tier__badge" style="background:${tier.color}">${tier.name[0]}</div>
        <div class="care-rewards-tier__name">${escapeHtml(tier.name)}</div>
        <div class="care-rewards-tier__threshold">${tier.threshold.toLocaleString()}+ points</div>
        <div class="care-rewards-tier__threshold">${tier.pointsMultiplier}x multiplier</div>
      </div>
    `).join('');
  }

  function bindActivityCodeButtons(container) {
    if (!container) return;
    container.querySelectorAll('[data-copy-code]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const code = btn.getAttribute('data-copy-code');
        if (!code) return;
        const original = btn.textContent;
        try {
          await navigator.clipboard.writeText(code);
          btn.textContent = 'Copied!';
        } catch {
          window.prompt('Copy your discount code:', code);
        }
        setTimeout(() => {
          btn.textContent = original;
        }, 2000);
      });
    });
  }

  function discountCodeForActivity(tx) {
    if (tx?.discountCode) return tx.discountCode;
    const match = String(tx?.description || '').match(/\b((?:REWARDS|REFER)-[A-Z0-9]+(?:-[A-Z0-9]+)*)\b/);
    return match ? match[1] : null;
  }

  function attachRedemptionCodesToActivity(transactions, redemptionCodes) {
    if (!Array.isArray(transactions) || !redemptionCodes?.length) {
      return transactions || [];
    }

    const redeemTransactions = transactions
      .filter((tx) => /^Redeemed:/i.test(tx.description || '') && !discountCodeForActivity(tx))
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    const availableCodes = [...redemptionCodes]
      .filter((entry) => entry?.code)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    const codeByTransactionId = new Map();
    for (let i = 0; i < redeemTransactions.length; i++) {
      const code = availableCodes[i]?.code;
      if (code) codeByTransactionId.set(redeemTransactions[i].id, code);
    }

    return transactions.map((tx) => {
      const code = discountCodeForActivity(tx) || codeByTransactionId.get(tx.id);
      return code ? { ...tx, discountCode: code } : tx;
    });
  }

  function renderActivity(transactions, container, redemptionCodes) {
    if (!container) return;
    const redirectPath = window.location.pathname;
    const activity = attachRedemptionCodesToActivity(transactions, redemptionCodes);

    if (!activity.length) {
      container.innerHTML = '<p class="care-rewards-activity__empty">No activity yet.</p>';
      return;
    }

    container.innerHTML = activity.map((tx) => {
      const discountCode = discountCodeForActivity(tx);
      const codeBlock = discountCode
        ? `
          <div class="care-rewards-activity__code">
            <code>${escapeHtml(discountCode)}</code>
            <div class="care-rewards-activity__code-actions">
              <button type="button" class="care-rewards-btn care-rewards-btn--secondary" data-copy-code="${String(discountCode).replace(/"/g, '&quot;')}">Copy code</button>
              <a href="/discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(redirectPath)}" class="care-rewards-btn care-rewards-btn--primary">Use code</a>
            </div>
          </div>
        `
        : '';

      return `
        <div class="care-rewards-activity__item">
          <div class="care-rewards-activity__details">
            <span class="care-rewards-activity__desc">${escapeHtml(tx.description)}</span>
            ${codeBlock}
          </div>
          <span class="care-rewards-activity__amount--${tx.amount > 0 ? 'positive' : 'negative'}">
            ${tx.amount > 0 ? '+' : ''}${tx.amount}
          </span>
        </div>
      `;
    }).join('');

    bindActivityCodeButtons(container);
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function showReferralWelcome(discountCode) {
    if (!discountCode) return;
    rememberReferralRedemptionCode(discountCode);

    const activityEl = document.getElementById('care-rewards-activity');
    if (!activityEl) return;

    document.getElementById('care-rewards-referral-welcome')?.remove();

    const banner = document.createElement('div');
    banner.id = 'care-rewards-referral-welcome';
    banner.className = 'care-rewards-referral-welcome';
    banner.innerHTML = `
      <p><strong>Referral applied!</strong> Your 1% discount code: <code>${escapeHtml(discountCode)}</code></p>
      <div class="care-rewards-referral-welcome__actions">
        <button type="button" class="care-rewards-btn care-rewards-btn--secondary" data-copy-referral-code="${escapeHtml(discountCode)}">Copy code</button>
        <a href="/cart" class="care-rewards-btn care-rewards-btn--primary">Apply on cart</a>
      </div>
    `;
    activityEl.insertAdjacentElement('afterend', banner);
    bindCopyReferralCodeButton(banner);
  }

  function setRewardsLoadingPlaceholders() {
    const loading =
      '<p class="care-rewards-activity__empty" style="margin:0;color:var(--cr-muted)">Loading…</p>';
    ['care-rewards-earn', 'care-rewards-redeem', 'care-rewards-vip'].forEach((id) => {
      const el = document.getElementById(id);
      if (el && !el.childElementCount) el.innerHTML = loading;
    });
  }

  function setRewardsLoadError(message) {
    const msg =
      message ||
      'Rewards could not load. Please refresh — if this keeps happening, try again in a minute.';
    const html = `<p class="care-rewards-activity__empty" style="margin:0">${escapeHtml(msg)}</p>`;
    ['care-rewards-earn', 'care-rewards-redeem', 'care-rewards-vip'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = html;
    });
  }

  async function initPage() {
    setRewardsLoadingPlaceholders();
    try {
      captureReferralCode();
      // Do not block the page on referral attach (can hang on cold proxy / bad stored ref).
      const referralPromise = attachStoredReferralOnce().catch((e) => {
        console.warn('Care Rewards referral:', e?.message || e);
        return null;
      });

      const data = await fetchLoyaltyData();
      const branding = data.branding || {};
      const isGuruPage = document.querySelector('.care-rewards-page--guru');
      if (!isGuruPage && branding.primaryColor) {
        document.documentElement.style.setProperty('--cr-primary', branding.primaryColor);
      }

      const earnEl = document.getElementById('care-rewards-earn');
      const earnOptions = {
        member: data.member,
        earnClaims: data.earnClaims || {},
        rules: data.earningRules || [],
        onRefresh: (result) => {
          if (result?.member?.pointsBalance != null) {
            const balanceEl = document.querySelector('.care-rewards-balance__amount');
            if (balanceEl) balanceEl.textContent = result.member.pointsBalance.toLocaleString();
          }
        },
      };
      renderEarnRules(data.earningRules || [], earnEl, earnOptions);

      const redeemEl = document.getElementById('care-rewards-redeem');
      const memberPoints = data.member?.pointsBalance || 0;
      renderRewards(data.rewards || [], redeemEl, memberPoints);
      bindRedeemButtons(redeemEl, memberPoints, window.location.pathname);
      renderVipTiers(
        data.vipTiers || [],
        document.getElementById('care-rewards-vip'),
        data.member?.vipTier?.id,
      );

      if (data.member) {
        const balanceEl = document.querySelector('.care-rewards-balance__amount');
        if (balanceEl) balanceEl.textContent = data.member.pointsBalance.toLocaleString();

        const referralEl = document.getElementById('care-rewards-referral-link');
        if (referralEl) {
          const link = buildReferralLink(data.member.referralCode);
          referralEl.textContent = link;
          const shareBtn = document.getElementById('care-rewards-share-referral');
          if (shareBtn) {
            shareBtn.setAttribute('data-share-referral', link);
            shareBtn.setAttribute('data-share-code', data.member.referralCode);
          }
          const codeBtn = document.getElementById('care-rewards-show-referral-code');
          if (codeBtn) {
            codeBtn.setAttribute('data-referral-code', data.member.referralCode);
          }
          const actions = document.querySelector('.care-rewards-referral .care-rewards-referral-actions');
          if (actions) {
            const display = ensureReferralCodeDisplay(actions);
            display.textContent = data.member.referralCode;
            display.hidden = true;
          }
          const qrWrap = document.getElementById('care-rewards-referral-qr');
          if (data.member.referralQrUrl) {
            if (qrWrap) {
              let img = qrWrap.querySelector('img');
              if (!img) {
                img = document.createElement('img');
                img.className = 'care-rewards-referral__qr';
                img.alt = 'Scan to share your referral link';
                img.width = 200;
                img.height = 200;
                img.loading = 'lazy';
                qrWrap.insertBefore(img, qrWrap.firstChild);
              }
              img.src = data.member.referralQrUrl;
              qrWrap.hidden = false;
            }
          } else {
            updateReferralQrImage(qrWrap, link);
          }
        }

        renderActivity(
          data.transactions || [],
          document.getElementById('care-rewards-activity'),
          data.member?.redemptionCodes,
        );

        const referralResult = await referralPromise;
        const welcomeCode = referralResult?.discountCode || data.member.refereeDiscountCode;
        if (welcomeCode) showReferralWelcome(welcomeCode);
      }
    } catch (e) {
      console.error('Care Rewards:', e);
      setRewardsLoadError(
        e?.message === 'Failed to load rewards'
          ? 'Rewards service is waking up or temporarily unavailable. Refresh in a moment.'
          : e?.message || 'Rewards could not load. Please refresh.',
      );
    }
  }

  function openLauncherPanel(panel) {
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
  }

  function closeLauncherPanel(panel) {
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
  }

  async function renderLauncherBody(body) {
    body.innerHTML = '<p class="care-rewards-launcher__loading">Loading rewards...</p>';
    try {
      const data = await fetchLoyaltyData();
      const pointsLabel =
        data.program?.pointsCurrencyPlural ||
        data.program?.pointsCurrencyName ||
        config.pointsLabel ||
        'Points';

      if (data.member) {
        body.innerHTML = `
          <div class="care-rewards-launcher__balance">
            <div class="care-rewards-launcher__balance-amount">${data.member.pointsBalance.toLocaleString()}</div>
            <div>${escapeHtml(pointsLabel)}</div>
            ${data.member.vipTier ? `<div style="margin-top:8px;color:${data.member.vipTier.color}">${escapeHtml(data.member.vipTier.name)} member</div>` : ''}
          </div>
          <p style="font-weight:600;margin-bottom:8px">Available rewards</p>
          ${(data.rewards || []).slice(0, 4).map((r) => `
            <div class="care-rewards-card" style="margin-bottom:8px;padding:12px">
              <strong>${escapeHtml(r.title)}</strong> — ${r.pointsCost} pts
            </div>
          `).join('') || '<p>No rewards available yet.</p>'}
          <a href="/pages/${escapeHtml(data.program?.loyaltyPageHandle || 'rewards')}" class="care-rewards-btn care-rewards-btn--primary" style="width:100%;text-align:center;margin-top:12px;display:block;text-decoration:none">View all rewards</a>
        `;
      } else {
        body.innerHTML = `
          <p>Join our rewards program and earn points on every purchase! <a href="/pages/${escapeHtml(data.program?.loyaltyPageHandle || 'rewards')}" class="care-rewards-launcher__see-rewards">See Rewards</a></p>
          <a href="/account/login?return_url=${encodeURIComponent(window.location.pathname)}" class="care-rewards-btn care-rewards-btn--primary" style="margin-top:16px;display:inline-block;text-decoration:none">Sign in</a>
          <a href="/account/register" class="care-rewards-btn" style="margin-top:12px;display:inline-block;text-decoration:none">Create account</a>
        `;
      }
      return data;
    } catch (e) {
      console.error('Care Rewards launcher:', e);
      body.innerHTML = '<p>Unable to load rewards. Please try again.</p>';
      return null;
    }
  }

  function applyLauncherProgramSettings(launcher, program) {
    if (!launcher || !program) return;

    if (program.launcherEnabled === false) {
      launcher.style.display = 'none';
      return false;
    }

    launcher.style.display = '';

    if (program.launcherPosition) {
      launcher.classList.remove('care-rewards-launcher--bottom-right', 'care-rewards-launcher--bottom-left');
      launcher.classList.add(`care-rewards-launcher--${program.launcherPosition}`);
    }

    const primary = program.launcherColor || config.primaryColor || GOLD;
    launcher.style.setProperty('--cr-primary', primary);
    launcher.style.setProperty('--cr-primary-dark', GOLD_DARK);
    launcher.style.setProperty('--cr-gold', primary);
    launcher.style.setProperty('--cr-gold-bright', '#e8c547');

    const header = launcher.querySelector('.care-rewards-launcher__header h3');
    if (header && program.programName) {
      header.textContent = program.programName;
    }

    return true;
  }

  async function initLauncher() {
    const launcher = document.getElementById('care-rewards-launcher');
    const btn = document.getElementById('care-rewards-launcher-btn');
    const panel = document.getElementById('care-rewards-launcher-panel');
    const body = document.getElementById('care-rewards-launcher-body');
    const close = document.getElementById('care-rewards-launcher-close');
    if (!btn || !panel || !body) return;

    if (launcher) {
      launcher.style.setProperty('--cr-primary', GOLD);
      launcher.style.setProperty('--cr-primary-dark', GOLD_DARK);
      launcher.style.setProperty('--cr-gold', GOLD);
      launcher.style.setProperty('--cr-gold-bright', '#e8c547');

      try {
        const data = await fetchLoyaltyData();
        if (!applyLauncherProgramSettings(launcher, data.program)) return;
      } catch (e) {
        console.warn('Care Rewards launcher settings:', e?.message || e);
      }
    }

    closeLauncherPanel(panel);

    btn.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      const isOpen = panel.classList.contains('is-open');
      if (isOpen) {
        closeLauncherPanel(panel);
        return;
      }
      openLauncherPanel(panel);
      await renderLauncherBody(body);
    });

    close?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeLauncherPanel(panel);
    });

    panel.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    document.addEventListener('click', (event) => {
      if (!panel.classList.contains('is-open')) return;
      if (panel.contains(event.target) || btn.contains(event.target)) return;
      closeLauncherPanel(panel);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeLauncherPanel(panel);
    });
  }

  function getUnusedRedemptionCodes(redemptionCodes) {
    return (redemptionCodes || []).filter((entry) => {
      if (!entry?.code) return false;
      if (entry.usedAt) return false;
      if (entry.status && entry.status !== 'active') return false;
      return true;
    });
  }

  async function applySubscriptionCode(discountCode) {
    const res = await fetch(`${proxyBaseUrl()}/loyalty?shop=${encodeURIComponent(config.shop)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        action: 'applySubscriptionCode',
        discountCode,
      }),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error('Rewards service is updating. Please try again in a minute.');
    }

    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Could not apply code to subscription');
    }

    return data;
  }

  function formatSubscriptionDate(value) {
    if (!value) return '';
    return new Date(value).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function buildCartRedeemSelectOptions(affordable, unusedCodes) {
    const parts = [];

    if (unusedCodes.length) {
      parts.push('<optgroup label="Your unused codes">');
      for (const entry of unusedCodes) {
        const label = entry.title ? `${entry.title} — ${entry.code}` : entry.code;
        parts.push(
          `<option value="code:${escapeHtml(entry.code)}">${escapeHtml(label)}</option>`,
        );
      }
      parts.push('</optgroup>');
    }

    if (affordable.length) {
      parts.push('<optgroup label="Redeem with points">');
      for (const reward of affordable) {
        parts.push(
          `<option value="${reward.id}">${escapeHtml(reward.title)} (${reward.pointsCost.toLocaleString()} pts)</option>`,
        );
      }
      parts.push('</optgroup>');
    }

    return parts.join('');
  }

  function shouldShowCartRedeem() {
    return window.location.pathname.includes('/cart');
  }

  function getCartMountPoint() {
    return (
      document.querySelector('form[action="/cart"]') ||
      document.querySelector('.cart__items') ||
      document.querySelector('main') ||
      document.body
    );
  }

  function bindCartReferralForm(widget, options = {}) {
    const form = widget.querySelector('[data-cart-referral-form]');
    const input = widget.querySelector('#care-rewards-cart-referral-input');
    const btn = widget.querySelector('#care-rewards-cart-referral-apply');
    if (!form || !input || !btn) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const code = normalizeReferralInput(input.value);
      if (!code) {
        showCartReferralMessage(widget, 'Enter a referral code.', 'error');
        return;
      }

      if (options.member?.hasReferrer) {
        showCartReferralMessage(widget, 'You have already applied a referral code.', 'error');
        return;
      }

      btn.disabled = true;
      const originalLabel = btn.textContent;
      btn.textContent = 'Applying...';
      widget.querySelector('.care-rewards-cart-widget__status')?.remove();

      try {
        const result = await applyReferralFromCart(code, widget);
        input.value = code;
        input.disabled = true;
        btn.disabled = true;
        btn.textContent = 'Applied';

        if (!result?.discountCode) {
          showCartReferralMessage(widget, 'Referral code applied to your account.', 'success');
        }
      } catch (e) {
        const message = e.message || 'Could not apply referral code';
        if (/sign in/i.test(message)) {
          localStorage.setItem(REF_STORAGE_KEY, code);
          showCartReferralMessage(
            widget,
            'Referral code saved. Sign in to apply your 1% first-order discount.',
            'success',
          );
          btn.disabled = false;
          btn.textContent = originalLabel;
          return;
        }
        btn.disabled = false;
        btn.textContent = originalLabel;
        showCartReferralMessage(widget, message, 'error');
      }
    });

    widget.querySelector('[data-cart-referral-apply-discount]')?.addEventListener('click', async (event) => {
      const discountBtn = event.currentTarget;
      let discountCode = discountBtn.getAttribute('data-cart-referral-apply-discount');

      discountBtn.disabled = true;
      discountBtn.textContent = 'Applying...';
      widget.querySelector('.care-rewards-cart-widget__status')?.remove();

      try {
        if (!discountCode) {
          const ensured = await ensureRefereeDiscountCode();
          discountCode = ensured.discountCode;
        }
        if (!discountCode) throw new Error('No referral discount code is available yet');
        await applyReferralDiscountToCart(discountCode, widget);
      } catch (e) {
        discountBtn.disabled = false;
        discountBtn.textContent = 'Apply referral discount';
        showCartReferralMessage(widget, e.message || 'Could not apply referral discount', 'error');
      }
    });
  }

  let cartDiscountBridgeReady = false;

  function initCartDiscountReferralBridge() {
    if (!shouldShowCartRedeem() || cartDiscountBridgeReady) return;
    cartDiscountBridgeReady = true;

    document.addEventListener(
      'submit',
      async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!form.classList.contains('cart-discount__form')) return;

        const input = form.querySelector('input[name="discount"]');
        if (!(input instanceof HTMLInputElement)) return;

        const value = input.value.trim();
        if (!looksLikeReferralCode(value)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const widget = document.getElementById('care-rewards-cart-referral');
        const referralInput = widget?.querySelector('#care-rewards-cart-referral-input');
        if (referralInput instanceof HTMLInputElement) {
          referralInput.value = normalizeReferralInput(value);
        }

        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn instanceof HTMLButtonElement) {
          submitBtn.disabled = true;
        }

        try {
          await applyReferralFromCart(value, widget);
          input.value = '';
        } catch (e) {
          if (widget) {
            showCartReferralMessage(widget, e.message || 'Could not apply referral code', 'error');
          } else {
            window.alert(e.message || 'Could not apply referral code');
          }
        } finally {
          if (submitBtn instanceof HTMLButtonElement) {
            submitBtn.disabled = false;
          }
        }
      },
      true,
    );
  }

  function renderCartReferralBody(widget, data) {
    const body = widget.querySelector('.care-rewards-cart-referral__body');
    if (!body) return;

    const member = data?.member;
    const pendingCode = localStorage.getItem(REF_STORAGE_KEY);
    const pendingValue = pendingCode ? normalizeReferralInput(pendingCode) : '';
    const appliedReferralCode = sessionStorage.getItem('care_rewards_applied_referral');

    if (member?.hasReferrer) {
      const discountCode = member.refereeDiscountCode;
      body.innerHTML = `
        <p class="care-rewards-cart-referral__message">You have already used a referral code. Only one referral can be applied per account.</p>
        ${
          discountCode
            ? `<button type="button" class="care-rewards-btn care-rewards-btn--primary care-rewards-cart-referral__apply-discount" data-cart-referral-apply-discount="${escapeHtml(discountCode)}">Apply referral discount</button>`
            : `<button type="button" class="care-rewards-btn care-rewards-btn--primary care-rewards-cart-referral__apply-discount" data-cart-referral-apply-discount="">Apply referral discount</button>`
        }
      `;
      bindCartReferralForm(widget, { member });
      return;
    }

    const inputValue = pendingValue || appliedReferralCode || '';
    const signedIn = Boolean(member);

    body.innerHTML = `
      <form class="care-rewards-cart-referral__controls" data-cart-referral-form>
        <label class="care-rewards-cart-referral__label" for="care-rewards-cart-referral-input">Referral code</label>
        <div class="care-rewards-cart-referral__row">
          <input
            id="care-rewards-cart-referral-input"
            class="care-rewards-cart-referral__input"
            type="text"
            name="referralCode"
            autocomplete="off"
            autocapitalize="characters"
            spellcheck="false"
            maxlength="32"
            placeholder="Enter code"
            value="${escapeHtml(inputValue)}"
          >
          <button type="submit" id="care-rewards-cart-referral-apply" class="care-rewards-btn care-rewards-btn--primary care-rewards-cart-referral__apply">Apply</button>
        </div>
      </form>
      <p class="care-rewards-cart-referral__hint">
        ${
          signedIn
            ? 'One referral code per account. You and your friend both earn rewards when you place your first order.'
            : 'Sign in after entering your code to unlock your 1% first-order discount.'
        }
      </p>
      ${
        !signedIn
          ? `<a href="/account/login?return_url=${encodeURIComponent(cartReturnPath())}" class="care-rewards-btn care-rewards-btn--secondary care-rewards-cart-referral__signin">Sign in to apply</a>`
          : ''
      }
    `;

    bindCartReferralForm(widget, { member });
  }

  async function initCartReferral() {
    if (!shouldShowCartRedeem()) return;
    if (document.getElementById('care-rewards-cart-referral')) return;

    const mount = getCartMountPoint();
    const widget = document.createElement('section');
    widget.id = 'care-rewards-cart-referral';
    widget.className = 'care-rewards-cart-referral page-width';
    widget.innerHTML = `
      <div class="care-rewards-cart-referral__inner">
        <div class="care-rewards-cart-referral__header">
          <h2 class="care-rewards-cart-referral__title">Referral code</h2>
          <p class="care-rewards-cart-referral__subtitle">Have a friend's 8-character Guru Rewards code? Enter it here for 1% off your first order — not in the discount code box below.</p>
        </div>
        <div class="care-rewards-cart-referral__body">
          <p class="care-rewards-cart-referral__loading">Loading referral options...</p>
        </div>
      </div>
    `;
    mount.insertAdjacentElement('afterbegin', widget);

    const appliedCode = sessionStorage.getItem('care_rewards_applied_code');
    if (appliedCode?.startsWith('REFER-')) {
      sessionStorage.setItem('care_rewards_applied_referral', normalizeReferralInput(appliedCode));
    }

    try {
      const referralResult = await attachStoredReferralOnce().catch((e) => {
        if (e?.message && !String(e.message).includes('already')) {
          showCartReferralMessage(widget, e.message, 'error');
        }
        return null;
      });

      const data = await fetchLoyaltyData();
      if (data.program?.referralEnabled === false) {
        widget.remove();
        return;
      }

      renderCartReferralBody(widget, data);

      const pendingDiscount =
        referralResult?.discountCode ||
        data.member?.refereeDiscountCode ||
        (appliedCode?.startsWith('REFER-') ? appliedCode : null);

      if (pendingDiscount) {
        try {
          const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((res) => res.json());
          const alreadyApplied = (cart.discount_codes || []).some(
            (entry) => entry.code === pendingDiscount && entry.applicable,
          );
          if (alreadyApplied) {
            confirmCartDiscountApplied(widget, pendingDiscount);
          } else {
            await applyReferralDiscountToCart(pendingDiscount, widget);
          }
        } catch (e) {
          showCartReferralMessage(widget, e.message || 'Could not apply referral discount', 'error');
        }
      }
    } catch (e) {
      const body = widget.querySelector('.care-rewards-cart-referral__body');
      if (body) {
        body.innerHTML = `
          <form class="care-rewards-cart-referral__controls" data-cart-referral-form>
            <label class="care-rewards-cart-referral__label" for="care-rewards-cart-referral-input">Referral code</label>
            <div class="care-rewards-cart-referral__row">
              <input
                id="care-rewards-cart-referral-input"
                class="care-rewards-cart-referral__input"
                type="text"
                name="referralCode"
                autocomplete="off"
                autocapitalize="characters"
                spellcheck="false"
                maxlength="32"
                placeholder="Enter code"
                value="${escapeHtml(normalizeReferralInput(localStorage.getItem(REF_STORAGE_KEY) || ''))}"
              >
              <button type="submit" id="care-rewards-cart-referral-apply" class="care-rewards-btn care-rewards-btn--primary care-rewards-cart-referral__apply">Apply</button>
            </div>
          </form>
          <p class="care-rewards-cart-referral__hint">One referral code per account.</p>
        `;
        bindCartReferralForm(widget, {});
      }
      console.error('Care Rewards cart referral:', e);
    }
  }

  async function initCartRedeem() {
    if (!shouldShowCartRedeem()) return;
    if (document.getElementById('care-rewards-cart-redeem')) return;

    const mount = getCartMountPoint();
    const referralWidget = document.getElementById('care-rewards-cart-referral');

    const widget = document.createElement('section');
    widget.id = 'care-rewards-cart-redeem';
    widget.className = 'care-rewards-cart-redeem page-width';
    widget.innerHTML = `
      <div class="care-rewards-cart-redeem__inner">
        <div class="care-rewards-cart-redeem__header">
          <h2 class="care-rewards-cart-redeem__title">Redeem Guru Rewards</h2>
          <p class="care-rewards-cart-redeem__subtitle">Apply your loyalty points as a discount on this order.</p>
        </div>
        <div class="care-rewards-cart-redeem__body">
          <p class="care-rewards-cart-redeem__loading">Loading your rewards...</p>
        </div>
      </div>
    `;
    if (referralWidget) {
      referralWidget.insertAdjacentElement('afterend', widget);
    } else {
      mount.insertAdjacentElement('afterbegin', widget);
    }

    const body = widget.querySelector('.care-rewards-cart-redeem__body');

    try {
      const data = await fetchLoyaltyData();
      const branding = data.branding || {};
      const pointsLabel = data.program?.pointsCurrencyPlural || 'Points';

      if (!getCustomerId() || !data.member) {
        body.innerHTML = `
          <p class="care-rewards-cart-redeem__message">Sign in to redeem your loyalty points at checkout.</p>
          <a href="/account/login?return_url=${encodeURIComponent('/cart')}" class="care-rewards-btn care-rewards-btn--primary">Sign in</a>
        `;
        return;
      }

      const affordable = (data.rewards || []).filter((r) => data.member.pointsBalance >= r.pointsCost);
      const unusedCodes = getUnusedRedemptionCodes(data.member.redemptionCodes);

      if (!affordable.length && !unusedCodes.length) {
        body.innerHTML = `
          <p class="care-rewards-cart-redeem__balance">Balance: <strong>${data.member.pointsBalance.toLocaleString()}</strong> ${escapeHtml(pointsLabel)}</p>
          <p class="care-rewards-cart-redeem__message">You need more points to redeem a reward. <a href="/pages/rewards">View ways to earn</a>.</p>
        `;
        return;
      }

      body.innerHTML = `
        <p class="care-rewards-cart-redeem__balance">Balance: <strong id="care-rewards-cart-balance">${data.member.pointsBalance.toLocaleString()}</strong> ${escapeHtml(pointsLabel)}</p>
        <div class="care-rewards-cart-redeem__controls">
          <label class="care-rewards-cart-redeem__label" for="care-rewards-cart-select">Choose a reward</label>
          <select id="care-rewards-cart-select" class="care-rewards-cart-redeem__select">
            ${buildCartRedeemSelectOptions(affordable, unusedCodes)}
          </select>
          <button type="button" id="care-rewards-cart-apply" class="care-rewards-btn care-rewards-btn--primary care-rewards-cart-redeem__apply">Apply to cart</button>
        </div>
        <p class="care-rewards-cart-redeem__hint">
          Your discount will be applied to this cart automatically. Unused codes also work on future
          subscription boxes — use &ldquo;Apply to next box&rdquo; if you have an active subscription.
        </p>
        ${
          data.member.subscription?.hasActiveSubscription
            ? `<button type="button" id="care-rewards-subscription-apply" class="care-rewards-btn care-rewards-btn--secondary care-rewards-cart-redeem__subscription">Apply selected code to next subscription box</button>`
            : ''
        }
      `;

      if (branding.primaryColor) {
        widget.style.setProperty('--cr-primary', branding.primaryColor);
      }

      const appliedCode = sessionStorage.getItem('care_rewards_applied_code');
      if (appliedCode) {
        sessionStorage.removeItem('care_rewards_applied_code');
        confirmCartDiscountApplied(widget, appliedCode);
      }

      document.getElementById('care-rewards-subscription-apply')?.addEventListener('click', async () => {
        const select = document.getElementById('care-rewards-cart-select');
        const btn = document.getElementById('care-rewards-subscription-apply');
        if (!select?.value || !btn) return;

        const code = select.value.startsWith('code:') ? select.value.slice(5) : null;
        if (!code) {
          showCartRedeemMessage(widget, 'Choose one of your unused codes first.', 'error');
          return;
        }

        btn.disabled = true;
        btn.textContent = 'Scheduling...';
        widget.querySelector('.care-rewards-cart-widget__status')?.remove();

        try {
          const result = await applySubscriptionCode(code);
          const billingNote = result.nextBillingDate
            ? ` It will apply on ${formatSubscriptionDate(result.nextBillingDate)}.`
            : '';
          showCartRedeemMessage(
            widget,
            `${result.message || 'Discount scheduled for your next subscription box.'}${billingNote}`,
            'success',
          );
        } catch (e) {
          showCartRedeemMessage(widget, e.message || 'Could not apply code to subscription', 'error');
        } finally {
          btn.disabled = false;
          btn.textContent = 'Apply selected code to next subscription box';
        }
      });

      document.getElementById('care-rewards-cart-apply')?.addEventListener('click', async () => {
        const select = document.getElementById('care-rewards-cart-select');
        const btn = document.getElementById('care-rewards-cart-apply');
        if (!select?.value || !btn) return;

        btn.disabled = true;
        btn.textContent = 'Applying...';
        widget.querySelector('.care-rewards-cart-widget__status')?.remove();

        try {
          if (select.value.startsWith('code:')) {
            await applyDiscountToCart(select.value.slice(5), cartReturnPath());
            return;
          }

          const result = await redeemReward(select.value, {
            redirectPath: cartReturnPath(),
            applyToCart: true,
          });
          const balanceEl = document.getElementById('care-rewards-cart-balance');
          if (balanceEl && result.member?.pointsBalance != null) {
            balanceEl.textContent = result.member.pointsBalance.toLocaleString();
          }
        } catch (e) {
          btn.disabled = false;
          btn.textContent = 'Apply to cart';
          showCartRedeemMessage(widget, e.message || 'Could not apply reward', 'error');
        }
      });
    } catch (e) {
      body.innerHTML = '<p class="care-rewards-cart-redeem__message">Unable to load rewards right now. Please refresh and try again.</p>';
      console.error('Care Rewards cart:', e);
    }
  }

  function boot() {
    initShareDelegation();
    initReferralCodeDelegation();
    captureReferralCode();
    attachStoredReferralOnce().catch((e) => {
      console.warn('Care Rewards referral:', e?.message || e);
    });
    if (document.getElementById('care-rewards-page')) initPage();
    if (config.launcher || document.getElementById('care-rewards-launcher')) initLauncher();
    initCartDiscountReferralBridge();
    initCartReferral();
    initCartRedeem();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();